import { useTranslation } from 'react-i18next'
import isEmpty from 'lodash-es/isEmpty'

import { QA_NAME_CANCEL_BTN, QA_NAME_SAVE_BTN } from '../../../../constants/qa'
import { SIZE } from '../../../../constants/size'
import { getHasFilesToShow } from '../../../../utils/file'
import Button from '../../../Button'
import {
  FileAttachTag,
  FileList,
  FileUpload,
  FileUploadPlaceholder,
} from '../../../File'
import { FormModalConfirmation, useCloseConfirm } from '../../../Form'
import Modal, { Box, Buttons, Content, Header } from '../../../Modal'
import { NotificationInlineList } from '../../../Notification'
import { TAG_HEIGHT } from '../../../Tag'
import Text from '../../../Text'

import {
  QA_NAME_VIEWING_DOCUMENT_UPLOAD_MODAL,
  VIEWING_DOCUMENT_MAX_SIZE_MB,
} from './RentalPortalViewingProfileDocumentUploadModal.constants'
import { useViewingDocumentUploadForm } from './RentalPortalViewingProfileDocumentUploadModal.hooks'
import { getViewingDocumentDropzoneOptions } from './RentalPortalViewingProfileDocumentUploadModal.utils'

import * as styles from './RentalPortalViewingProfileDocumentUploadModal.module.css'

function RentalPortalViewingProfileDocumentUploadModal({
  title,
  viewingId,
  onClose,
  onUploaded,
}) {
  const { t } = useTranslation()

  const {
    files,
    filesError,
    filesUploading,
    handleGetFiles,
    handleRemoveFile,
    handleSave,
    handleStartUploading,
    handleUploadErrors,
    handleUploadFile,
    isSaving,
    notifications,
  } = useViewingDocumentUploadForm({ viewingId, onClose, onUploaded })

  const { handleClose, handleCloseConfirmation, isCloseConfirm } =
    useCloseConfirm({ onClose })

  function handleCloseWithConfirm() {
    handleClose({ dirty: files.length > 0 || filesError.length > 0 })
  }

  function renderFile(file, key) {
    function handleRemove() {
      handleRemoveFile(file.id)
    }

    return (
      <FileAttachTag
        file={file}
        height={TAG_HEIGHT.height40}
        key={key}
        onRemove={handleRemove}
      />
    )
  }

  return (
    <>
      <Modal
        qaName={QA_NAME_VIEWING_DOCUMENT_UPLOAD_MODAL}
        onClose={handleCloseWithConfirm}
      >
        <Box size={SIZE.LARGE}>
          <Header
            text={t('RentalPortal.ViewingDocumentUploadTitleKey', {
              viewing: title,
            })}
            onClose={handleCloseWithConfirm}
          />
          <Content
            contentWrapperClassName={styles.contentWrapper}
            size={SIZE.LARGE}
          >
            {isEmpty(notifications) ? null : (
              <NotificationInlineList items={notifications} />
            )}
            <FileUpload
              className={styles.dropzone}
              getFiles={handleGetFiles}
              options={getViewingDocumentDropzoneOptions()}
              onFinishUpload={handleUploadFile}
              onStartUpload={handleStartUploading}
              onUploadErrors={handleUploadErrors}
            >
              <FileUploadPlaceholder
                className={styles.placeholder}
                text={t('RentalPortal.ViewingDocumentUploadDropzoneHintKey')}
              />
            </FileUpload>
            <Text isSecondary>
              {t('RentalPortal.ViewingDocumentUploadRestrictionsKey', {
                size: VIEWING_DOCUMENT_MAX_SIZE_MB,
              })}
            </Text>
            {getHasFilesToShow({ files, filesError, filesUploading }) ? (
              <FileList
                className={styles.files}
                errorItems={filesError}
                files={files}
                itemClassName={styles.file}
                renderItem={renderFile}
                uploadingItems={filesUploading}
              />
            ) : null}
          </Content>
          <Buttons>
            <Button.Contained
              disabled={isEmpty(files)}
              isLoading={isSaving}
              qaName={QA_NAME_SAVE_BTN}
              text={t('Modal.SaveBtnKey')}
              onClick={handleSave}
            />
            <Button.Outlined
              qaName={QA_NAME_CANCEL_BTN}
              text={t('Modal.CancelBtnKey')}
              onClick={handleCloseWithConfirm}
            />
          </Buttons>
        </Box>
      </Modal>
      {isCloseConfirm ? (
        <FormModalConfirmation
          onCancel={handleCloseConfirmation}
          onSubmit={onClose}
        />
      ) : null}
    </>
  )
}

export default RentalPortalViewingProfileDocumentUploadModal
